import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { getPlanTransferList, updateWorkflow } from '../../features/adviceAdmin';
import { useTranslation } from 'react-i18next';

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

// API Approval type
export type ApiApproval = {
    _id: string;
    clientId: string;
    created_at: string;
    created_by: string;
    from_user: string;
    from_user_email: string;
    from_user_name: string;
    status: ApprovalStatus | string;
    to_user: string;
    to_user_email: string;
    to_user_name: string;
    workflowType: string;
    __meta: {
        action: string;
        author: string;
        comment: string;
        id: string;
        timestamp: string;
        user: string;
    };
};

// UI Approval type for PortfolioApprovalsOrganism
export type Approval = {
    id: string;
    date: string;
    submitter: string;
    userId: string;
    tenant: string;
    property: string;
    fromUser: string;
    fromUserEmail: string;
    fromUserName: string;
    toUser: string;
    toUserEmail: string;
    toUserName: string;
    workflowType: string;
    status: ApprovalStatus | string;
};

export function usePlanTransferApprovals() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [apiApprovals, setApiApprovals] = useState<ApiApproval[]>([]);
    const advisorAdminLoading = useAppSelector((state) => state?.advisorAdmin?.loading);
    const dataExists = useAppSelector((state: any) => state.advisorAdmin?.adminsAndAdvisors?.length > 0);

    const [isLoading, setIsLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState<{ [id: string]: boolean }>({});
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        type: 'success' | 'error' | null;
        icon?: React.ReactNode;
    }>({ open: false, message: '', type: null });

    // get plan transfer approvals list
    console.log('Fetching plan transfer approvals', dataExists, advisorAdminLoading);
    useEffect(() => {
        setIsLoading(true);

        dispatch(getPlanTransferList())
            .then(async (res: any) => {
                setApiApprovals(res?.payload?.data_list || []);
            })
            .catch((err: any) => {
                setApiApprovals([]);
                console.error('API failed', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [dispatch, advisorAdminLoading]);

    const refreshApprovals = async () => {
        setIsLoading(true);
        try {
            const res = await dispatch(getPlanTransferList());
            setApiApprovals(res?.payload?.data_list || []);
        } catch (err) {
            setApiApprovals([]);
            console.error('API failed', err);
        } finally {
            setIsLoading(false);
        }
    };

    const approve = async (id: string, userName: string = '') => {
        setActionLoading((prev) => ({ ...prev, [id]: true }));
        try {
            const result = await dispatch(
                updateWorkflow({ workflow_id: id, action: 'APPROVED', workflow_type: 'RemappingClient' })
            );
            if (result.meta && result.meta.requestStatus === 'fulfilled') {
                setApiApprovals((prev) => prev.filter((a) => a._id !== id));
                setSnackbar({
                    open: true,
                    message: t('PLAN_TRANSFER_APPROVED', { userName: userName }),
                    type: 'success'
                });
            }
        } finally {
            setActionLoading((prev) => ({ ...prev, [id]: false }));
        }
    };

    const reject = async (id: string, userName: string = '') => {
        setActionLoading((prev) => ({ ...prev, [id]: true }));
        try {
            const result = await dispatch(
                updateWorkflow({ workflow_id: id, action: 'REJECTED', workflow_type: 'RemappingClient' })
            );
            if (result.meta && result.meta.requestStatus === 'fulfilled') {
                setApiApprovals((prev) => prev.filter((a) => a._id !== id));
                setSnackbar({
                    open: true,
                    message: t('PLAN_TRANSFER_REJECTED', { userName: userName }),
                    type: 'error'
                });
            }
        } finally {
            setActionLoading((prev) => ({ ...prev, [id]: false }));
        }
    };

    // The property is fixed now as below
    const propertyType = 'Digital Advisor';

    // Map API approvals to UI approvals
    const mapApiToUi = (a: ApiApproval): Approval => ({
        id: a._id,
        date: a.created_at,
        submitter: a.__meta.author,
        tenant: a.clientId,
        property: propertyType,
        fromUser: a.from_user,
        fromUserEmail: a.from_user_email,
        fromUserName: a.from_user_name,
        toUser: a.to_user,
        toUserEmail: a.to_user_email,
        toUserName: a.to_user_name,
        workflowType: a.workflowType,
        status: a.status,
        userId: a.__meta.user
    });

    const pendingApprovals = apiApprovals.filter((a) => a.status === 'PENDING').map(mapApiToUi);

    const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));
    return {
        pendingApprovals,
        approve,
        reject,
        isLoading,
        actionLoading,
        snackbar,
        handleSnackbarClose,
        refreshApprovals
    };
}
